/**
 * Supra Sneakers — Predictive Search enhancement
 *
 * Adds a rich dropdown to every search input on the site:
 *   - Shows product thumbnails, titles, prices
 *   - Groups results: Products, Collections, Pages
 *   - Cleans up the "raw text" dump the theme was producing
 *
 * Works by intercepting any <input type="search">, <input name="q">, or
 * elements matching [data-predictive-search] and attaching its own UI.
 */
(function () {
  'use strict';

  var DROPDOWN_ID = 'supra-predict-dropdown';
  var DEBOUNCE_MS = 140;
  var MIN_LEN = 2;

  var styleEl = document.createElement('style');
  styleEl.textContent = `
    .supra-predict {
      position: absolute;
      z-index: 9999;
      background: #fff;
      color: #111;
      border: 1px solid rgba(0,0,0,0.08);
      border-radius: 14px;
      box-shadow: 0 24px 60px rgba(0,0,0,0.18);
      max-width: 560px;
      min-width: 320px;
      max-height: 70vh;
      overflow-y: auto;
      padding: 8px;
      font-family: inherit;
    }
    .supra-predict__group {
      padding: 6px 10px 10px;
    }
    .supra-predict__group + .supra-predict__group {
      border-top: 1px solid rgba(0,0,0,0.06);
    }
    .supra-predict__heading {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      color: rgba(0,0,0,0.55);
      padding: 8px 6px 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .supra-predict__heading a {
      font-size: 11px;
      color: #111;
      text-decoration: none;
      font-weight: 600;
    }
    .supra-predict__item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 6px;
      border-radius: 10px;
      text-decoration: none;
      color: inherit;
    }
    .supra-predict__item:hover, .supra-predict__item.is-active {
      background: #f5f5f6;
    }
    .supra-predict__thumb {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      background: #f1f1f2 center/cover no-repeat;
      flex: 0 0 auto;
    }
    .supra-predict__body { min-width: 0; flex: 1; }
    .supra-predict__title {
      font-size: 13px;
      font-weight: 600;
      line-height: 1.25;
      margin: 0 0 2px;
      color: #111;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .supra-predict__vendor {
      font-size: 11px;
      color: rgba(0,0,0,0.55);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .supra-predict__price {
      font-size: 13px;
      font-weight: 600;
      color: #111;
      text-align: right;
      flex: 0 0 auto;
    }
    .supra-predict__price del { color: rgba(0,0,0,0.45); font-weight: 400; margin-right: 6px; }
    .supra-predict__empty {
      padding: 20px;
      text-align: center;
      color: rgba(0,0,0,0.6);
      font-size: 13px;
    }
    .supra-predict__loading {
      padding: 14px;
      text-align: center;
      color: rgba(0,0,0,0.55);
      font-size: 12px;
    }
    .supra-predict__view-all {
      display: block;
      text-align: center;
      padding: 12px;
      margin-top: 6px;
      background: #111;
      color: #fff;
      border-radius: 10px;
      text-decoration: none;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      font-size: 12px;
    }
  `;
  document.head.appendChild(styleEl);

  var dropdown = null;
  var activeInput = null;
  var debounceTimer = null;

  function ensureDropdown() {
    if (dropdown) return dropdown;
    dropdown = document.createElement('div');
    dropdown.className = 'supra-predict';
    dropdown.id = DROPDOWN_ID;
    dropdown.style.display = 'none';
    document.body.appendChild(dropdown);
    return dropdown;
  }

  function positionDropdown(input) {
    if (!dropdown) return;
    var rect = input.getBoundingClientRect();
    var top = rect.bottom + window.scrollY + 6;
    var left = rect.left + window.scrollX;
    dropdown.style.top = top + 'px';
    dropdown.style.left = left + 'px';
    dropdown.style.width = Math.max(rect.width, 320) + 'px';
  }

  function hideDropdown() {
    if (!dropdown) return;
    dropdown.style.display = 'none';
    dropdown.innerHTML = '';
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function renderResults(data, query) {
    var groups = [];
    var products = (data.resources && data.resources.results && data.resources.results.products) || [];
    var collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
    var pages = (data.resources && data.resources.results && data.resources.results.pages) || [];

    if (products.length > 0) {
      var list = products.slice(0, 6).map(function (p) {
        var img = (p.featured_image && p.featured_image.url) || (p.image) || '';
        var price = money(p.price_min || p.price);
        var comparePrice = p.compare_at_price_min && p.compare_at_price_min > (p.price_min || 0) ? money(p.compare_at_price_min) : '';
        return `
          <a class="supra-predict__item" href="${p.url}">
            <span class="supra-predict__thumb" style="background-image: url('${img}')"></span>
            <span class="supra-predict__body">
              <p class="supra-predict__title">${escapeHtml(p.title)}</p>
              <span class="supra-predict__vendor">${escapeHtml(p.vendor || '')}</span>
            </span>
            <span class="supra-predict__price">${comparePrice ? '<del>' + comparePrice + '</del>' : ''}${price}</span>
          </a>
        `;
      }).join('');
      groups.push(`
        <div class="supra-predict__group">
          <div class="supra-predict__heading">
            <span>Products</span>
            <a href="/search?q=${encodeURIComponent(query)}&type=product">See all →</a>
          </div>
          ${list}
        </div>
      `);
    }

    if (collections.length > 0) {
      var clist = collections.slice(0, 3).map(function (c) {
        return `<a class="supra-predict__item" href="${c.url}"><span class="supra-predict__body"><p class="supra-predict__title">${escapeHtml(c.title)}</p></span></a>`;
      }).join('');
      groups.push(`
        <div class="supra-predict__group">
          <div class="supra-predict__heading"><span>Collections</span></div>
          ${clist}
        </div>
      `);
    }

    if (pages.length > 0) {
      var plist = pages.slice(0, 3).map(function (p) {
        return `<a class="supra-predict__item" href="${p.url}"><span class="supra-predict__body"><p class="supra-predict__title">${escapeHtml(p.title)}</p></span></a>`;
      }).join('');
      groups.push(`
        <div class="supra-predict__group">
          <div class="supra-predict__heading"><span>Help / Pages</span></div>
          ${plist}
        </div>
      `);
    }

    if (groups.length === 0) {
      return `<div class="supra-predict__empty">No matches for "${escapeHtml(query)}". Try a brand or model name.</div>`;
    }

    return groups.join('') + `<a class="supra-predict__view-all" href="/search?q=${encodeURIComponent(query)}">See all results for "${escapeHtml(query)}"</a>`;
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[c];
    });
  }

  function search(query, input) {
    ensureDropdown();
    positionDropdown(input);
    dropdown.innerHTML = '<div class="supra-predict__loading">Searching…</div>';
    dropdown.style.display = 'block';

    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product,collection,page'
      + '&resources[limit]=6&resources[limit_scope]=each'
      + '&resources[options][fields]=title,product_type,variants.title,vendor,tag';

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (activeInput !== input) return; // ignore stale
        dropdown.innerHTML = renderResults(data, query);
      })
      .catch(function () {
        dropdown.innerHTML = '<div class="supra-predict__empty">Search is unavailable right now.</div>';
      });
  }

  function onInput(e) {
    var input = e.target;
    activeInput = input;
    var q = (input.value || '').trim();
    clearTimeout(debounceTimer);
    if (q.length < MIN_LEN) { hideDropdown(); return; }
    debounceTimer = setTimeout(function () { search(q, input); }, DEBOUNCE_MS);
  }

  function onFocus(e) {
    var input = e.target;
    activeInput = input;
    var q = (input.value || '').trim();
    if (q.length >= MIN_LEN) { search(q, input); }
  }

  function onBlur() {
    // Give clicks inside the dropdown a chance to register
    setTimeout(function () {
      if (!dropdown) return;
      if (document.activeElement && dropdown.contains(document.activeElement)) return;
      hideDropdown();
    }, 150);
  }

  function attach() {
    var inputs = document.querySelectorAll('input[type="search"], input[name="q"], [data-predictive-search-input]');
    inputs.forEach(function (input) {
      if (input.dataset.supraPredictBound === 'true') return;
      input.dataset.supraPredictBound = 'true';
      input.setAttribute('autocomplete', 'off');
      input.addEventListener('input', onInput);
      input.addEventListener('focus', onFocus);
      input.addEventListener('blur', onBlur);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') hideDropdown();
      });
    });
    window.addEventListener('scroll', function () { if (activeInput) positionDropdown(activeInput); }, { passive: true });
    window.addEventListener('resize', function () { if (activeInput) positionDropdown(activeInput); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }

  // Re-bind whenever new search inputs appear (e.g., from drawer open)
  var observer = new MutationObserver(function () { attach(); });
  observer.observe(document.body, { childList: true, subtree: true });
})();
